import React, { useState, useEffect } from 'react';
import { Eye, Check, Clock, Package, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600';
      case 'shipped': return 'text-blue-600';
      case 'delivered': return 'text-emerald-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-neutral-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return Check;
      case 'shipped': return Truck;
      case 'delivered': return Package;
      default: return Clock;
    }
  };

  return (
    <div data-testid="admin-orders" className="p-6 lg:p-10">
      <div className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight">
          Orders
        </h1>
        <p className="mt-2 text-neutral-500">Track and manage customer orders</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-neutral-500">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const StatusIcon = getStatusIcon(order.order_status);
            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-neutral-200 p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-neutral-400">#{order.id.slice(0, 8)}</span>
                      <span className={`flex items-center gap-1 text-sm ${getStatusColor(order.order_status)}`}>
                        <StatusIcon size={14} />
                        {order.order_status.toUpperCase()}
                      </span>
                    </div>
                    <p className="mt-2 font-medium">{order.customer_name}</p>
                    <p className="text-neutral-500 text-sm">{order.customer_email}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold">
                        £{order.total.toFixed(2)}
                      </p>
                      <p className="text-neutral-500 text-sm">
                        {order.items.length} item{order.items.length > 1 ? 's' : ''}
                      </p>
                    </div>

                    <button
                      onClick={() => setSelectedOrder(order)}
                      data-testid={`view-order-${order.id}`}
                      className="p-3 border border-neutral-200 hover:border-black transition-colors"
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                </div>

                {/* Quick Status Update */}
                <div className="mt-4 pt-4 border-t border-neutral-100 flex gap-2 flex-wrap">
                  {['pending', 'confirmed', 'shipped', 'delivered'].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateOrderStatus(order.id, status)}
                      data-testid={`status-${status}-${order.id}`}
                      className={`px-3 py-1 text-xs uppercase tracking-wider border transition-colors ${
                        order.order_status === status
                          ? 'border-[#e84628] bg-[#e84628] text-white'
                          : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedOrder(null)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl bg-white border border-neutral-200 z-50 overflow-y-auto max-h-[90vh]"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold tracking-tight">
                    Order Details
                  </h2>
                  <button onClick={() => setSelectedOrder(null)} className="text-neutral-400 hover:text-black">
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider">Order ID</p>
                    <p className="mt-1">{selectedOrder.id}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-neutral-400 text-sm uppercase tracking-wider">Customer</p>
                      <p className="mt-1">{selectedOrder.customer_name}</p>
                      <p className="text-neutral-500 text-sm">{selectedOrder.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 text-sm uppercase tracking-wider">Payment</p>
                      <p className="mt-1 capitalize">{selectedOrder.payment_method}</p>
                      <p className={`text-sm ${selectedOrder.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                        {selectedOrder.payment_status}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider mb-2">Shipping Address</p>
                    <p>{selectedOrder.shipping_address.line1}</p>
                    {selectedOrder.shipping_address.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                    <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.postal_code}</p>
                    <p>{selectedOrder.shipping_address.country}</p>
                  </div>

                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider mb-4">Items</p>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item, index) => (
                        <div key={index} className="flex items-center gap-4 p-3 bg-neutral-50">
                          <div className="w-12 h-12 bg-neutral-200">
                            {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{item.name}</p>
                            <p className="text-neutral-500 text-sm">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-medium">£{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-neutral-200 flex justify-between">
                    <span className="text-neutral-500 uppercase tracking-wider">Total</span>
                    <span className="text-2xl font-bold">
                      £{selectedOrder.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOrders;
